import Head from 'next/head'
import React, { useState, useEffect, useRef, useContext } from 'react'
import AppContext from '../context/AppContext'
import Button from '../components/Common/Button/Button'
import styles from '../styles/SalesCRM.module.scss'
import homeStyles from '../styles/Home.module.scss'
import { LeadSystemVisual } from '../components/Home/Component3/LeadSystemVisual'

const HERO_FEATURES: { label: string; icon: React.ReactNode }[] = [
  { label: 'Leads & Pipeline', icon: <><path d="M4 6h16M7 12h10M10 18h4" /></> },
  { label: 'Incentive Automation', icon: <><circle cx="12" cy="12" r="8" /><path d="M12 8v8M9.5 10h3.5a1.8 1.8 0 0 1 0 3.6H10a1.8 1.8 0 0 0 0 3.6h3.5" /></> },
  { label: 'Admin Review', icon: <><path d="M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6z" /><path d="M9 12l2 2 4-4" /></> },
  { label: 'Leaderboards', icon: <><path d="M3 21h18" /><rect x="5" y="11" width="3" height="7" /><rect x="10.5" y="6" width="3" height="12" /><rect x="16" y="13" width="3" height="5" /></> },
  { label: 'Reports', icon: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h3" /></> },
  { label: 'Config', icon: <><circle cx="12" cy="12" r="3.2" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" /></> },
]
const C = {
  ind: '#2563EB',
  vio: '#36B1F2',
  grn: '#0EA5E9',
  amb: '#60A5FA',
  red: '#1E40AF',
  sky: '#0284C7',
  pink: '#6366F1',
  teal: '#06B6D4',
  ink: '#0D0D0D',
  paper: '#FAFAF7',
  t1: '#111827',
  t2: '#4B5563',
  t3: '#9CA3AF',
  br: '#E5E7EB',
  br2: '#D1D5DB',
  bg: '#FAFAF7',
  bg3: '#F4F4F8',
  bg4: '#EBEBF4',
  grad: 'linear-gradient(116.56deg, #2563EB 0%, #36B1F2 100%)',
}

const MODULES = [
  { id: 'dashboard', icon: '📊', label: 'Dashboard', color: '#2563EB', bg: '#EEF2FF', title: 'Your command centre', desc: 'Real-time KPIs across all stores — total leads, revenue, conversion rate and a live 6-month trend chart. The entire business in one glance.', stats: [{ v: '248', l: 'Active Leads' }, { v: '7.2L', l: 'Revenue' }, { v: '12.5%', l: 'Conv. Rate' }, { v: '14', l: 'New Today' }] },
  { id: 'leads', icon: '🎯', label: 'Lead Management', color: '#36B1F2', bg: '#F0F9FF', title: 'Never lose a lead again', desc: 'Every enquiry — website, walk-in, referral, cold call — captured instantly. Pipeline from New to Contacted to Qualified to Converted.', stats: [{ v: '6', l: 'Stages' }, { v: '5', l: 'Sources' }, { v: '5', l: 'Regions' }, { v: 'Live', l: 'Assignment' }] },
  { id: 'orders', icon: '📦', label: 'Orders', color: '#0EA5E9', bg: '#EFF6FF', title: 'Lead to order, automatically', desc: 'The moment a lead converts, a complete order record is created — no data entry, no handoff. Every order carries the full story: customer, product, executive, store.', stats: [{ v: '2.95L', l: 'Period Revenue' }, { v: '59K', l: 'Avg Order' }, { v: '0', l: 'Manual Entry' }, { v: '100%', l: 'Tracked' }] },
  { id: 'incentives', icon: '💰', label: 'Incentives', color: '#1D4ED8', bg: '#EBF5FF', title: 'Automatic. Accurate. Instant.', desc: 'Every sale triggers incentive calculation across all 5 roles — SE, Store Manager, Cluster, Territory, Area Manager. Credited the moment an order is marked complete.', stats: [{ v: '54.2K', l: 'Top SE Month' }, { v: '5', l: 'Role Levels' }, { v: '0', l: 'Manual Calc' }, { v: 'Live', l: 'Credited' }] },
  { id: 'logs', icon: '📋', label: 'Incentive Logs', color: '#1E40AF', bg: '#EEF2FF', title: 'Full audit trail, always', desc: 'Every payout ever generated — order, employee, role, rule applied, percentage, amount, status. Filter by employee, date or role. Export to CSV in one click.', stats: [{ v: '100%', l: 'Auditable' }, { v: 'CSV', l: 'Export' }, { v: 'Live', l: 'Filters' }, { v: 'inf', l: 'History' }] },
  { id: 'review', icon: '✅', label: 'Incentive Review', color: '#6366F1', bg: '#F5F5FF', title: 'Finance controls that work', desc: 'Admin approval layer before any payout credits. Review, approve, flag or reject. High-value payouts held for manual review. Routine payouts auto-approved.', stats: [{ v: 'Auto', l: 'Smart Queue' }, { v: 'Flag', l: 'Dispute Flow' }, { v: '2-click', l: 'Approval' }, { v: 'Locked', l: 'Audit' }] },
  { id: 'leaderboard', icon: '🏆', label: 'Leaderboard', color: '#0284C7', bg: '#F0F9FF', title: 'Visibility drives performance', desc: 'Sales Executive rankings by revenue. Store-vs-store comparison. Quarterly targets per store, per category. When the team sees the numbers in real time, they push.', stats: [{ v: '5', l: 'Stores Ranked' }, { v: 'Q1-Q4', l: 'Quarterly' }, { v: 'Live', l: 'Rankings' }, { v: '4', l: 'Categories' }] },
  { id: 'rules', icon: '⚙️', label: 'Incentive Rules', color: '#36B1F2', bg: '#F0F9FF', title: 'Your policy, your logic', desc: 'Define the rules that drive every automatic payout — Enterprise Bonus, Premium Plan, Basic Plan. Each rule independently configured with per-role percentages.', stats: [{ v: '3+', l: 'Rule Types' }, { v: 'Versioned', l: 'History' }, { v: 'Live', l: 'Activation' }, { v: 'Per-role', l: 'Rates' }] },
  { id: 'reports', icon: '📈', label: 'Reports', color: '#0EA5E9', bg: '#F0F9FF', title: 'Analytics for real questions', desc: 'Lead source breakdown. Incentive disbursement by role. Store performance summaries. SE rankings. Quarterly reviews. Export PDF or auto-deliver to leadership.', stats: [{ v: '5+', l: 'Report Types' }, { v: 'PDF', l: 'Export' }, { v: 'Scheduled', l: 'Auto-deliver' }, { v: 'Live', l: 'Data' }] },
  { id: 'config', icon: '🔧', label: 'Configuration', color: '#2563EB', bg: '#EEF2FF', title: 'Built for retail store', desc: 'Warranty, Trade-In, Loans, Accessories — each with its own independently configured payout schedule.', stats: [{ v: '4', l: 'Categories' }, { v: 'Locked', l: 'Closed Qtrs' }, { v: 'Per-role', l: 'Slabs' }, { v: 'inf', l: 'Flexibility' }] },
  { id: 'hierarchy', icon: '🏢', label: 'Org Hierarchy', color: '#0EA5E9', bg: '#EBEBF4', title: '5-tier visibility, zero gaps', desc: 'Zone Head to Regional Manager to Cluster Manager to Store Manager to Sales Executive. Add a team member, assign a store, set targets — every system reflects it immediately.', stats: [{ v: '5', l: 'Tier Levels' }, { v: '5', l: 'Cities' }, { v: 'Instant', l: 'Updates' }, { v: 'Full', l: 'Visibility' }] },
]

const CASCADE_ROLES = [
  { role: 'Sales Executive', pct: '6%', amt: '7,200', color: '#2563EB', w: 100 },
  { role: 'Store Manager', pct: '2.5%', amt: '3,000', color: '#36B1F2', w: 60 },
  { role: 'Cluster Manager', pct: '1.5%', amt: '1,800', color: '#0EA5E9', w: 36 },
  { role: 'Territory Manager', pct: '0.75%', amt: '900', color: '#1D4ED8', w: 18 },
  { role: 'Area Manager', pct: '0.375%', amt: '450', color: '#1E40AF', w: 9 },
]

const STORES = [
  { city: 'Koramangala', state: 'Bangalore', revenue: '1.2L', rank: 1, pct: 100 },
  { city: 'Andheri', state: 'Mumbai', revenue: '80K', rank: 2, pct: 67 },
  { city: 'T.Nagar', state: 'Chennai', revenue: '67K', rank: 3, pct: 56 },
  { city: 'FC Road', state: 'Pune', revenue: '35K', rank: 4, pct: 29 },
  { city: 'CP Store', state: 'New Delhi', revenue: '28K', rank: 5, pct: 23 },
]

const FEATURES = [
  { icon: '⚡', title: 'Instant incentive credit', desc: 'Payouts triggered the moment an order is marked complete — no waiting, no manual processing.' },
  { icon: '🔒', title: 'Quarter locking', desc: 'Once a quarter closes, every slab and payout record is permanently locked. The record is the record.' },
  { icon: '📊', title: 'Scheduled reports', desc: 'Weekly digests to Zone Heads. Monthly summaries to all managers. Auto-delivered, always accurate.' },
  { icon: '🎯', title: 'Achievement slabs', desc: '60 to 80 percent achievement earns one rate. Cross 80 percent and it jumps. Slab system drives performance.' },
  { icon: '🔍', title: 'Admin review queue', desc: 'High-value payouts held for human review. Routine amounts auto-approved. Finance stays in control.' },
  { icon: '🏆', title: 'Live leaderboard', desc: 'Every Sales Executive sees their ranking in real time. Transparency creates healthy competition.' },
  { icon: '🗂', title: 'Per-category config', desc: 'Warranty, Trade-In, Loans, Accessories — each with its own independently configured payout schedule.' },
  { icon: '🌐', title: '5-city connected', desc: 'Koramangala, Andheri, T.Nagar, FC Road, Connaught Place — one platform, one truth.' },
]

function useVisible(ref: React.RefObject<HTMLElement>) {
  const [v, setV] = useState(false)
  useEffect(() => {
    const el = ref.current; if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setV(true); obs.disconnect() } },
      { threshold: 0.06 }
    )
    obs.observe(el); return () => obs.disconnect()
  }, [])
  return v
}

function DashViz() {
  return (
    <div className={styles.dashViz}>
      <div className={styles.header}>
        <div className={styles.dots}>
          {['#ff5f57', '#febc2e', '#28c840'].map(c => <div key={c} style={{ background: c }} />)}
        </div>
        <div className={styles.title}>SalesCRM — Dashboard</div>
      </div>
      <div className={styles.body}>
        <div className={styles.grid}>
          {[['248', 'Leads', '#2563EB'], ['7.2L', 'Revenue', '#0EA5E9'], ['12.5%', 'Conv.', '#60A5FA'], ['14', 'New Today', '#0284C7']].map(([v, l, c]) => (
            <div key={l} className={styles.item}>
              <div className={styles.v} style={{ color: c as string }}>{v}</div>
              <div className={styles.l}>{l}</div>
            </div>
          ))}
        </div>
        <div className={styles.funnel}>
          {[['New', 100, '#2563EB', '248'], ['Contacted', 75, '#36B1F2', '186'], ['Qualified', 38, '#0EA5E9', '94'], ['Converted', 12, '#60A5FA', '31']].map(([l, w, c, n]) => (
            <div key={l as string} className={styles.row}>
              <div className={styles.l}>{l as string}</div>
              <div className={styles.bar}>
                <div style={{ width: `${w}%`, background: c as string }} />
              </div>
              <div className={styles.n}>{n}</div>
            </div>
          ))}
        </div>
        <div className={styles.chart}>
          <svg viewBox="0 0 300 52" fill="none" style={{ width: '100%', height: 52 }}>
            <defs><linearGradient id="sg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity="0.22" /><stop offset="100%" stopColor="#2563EB" stopOpacity="0" /></linearGradient></defs>
            <path d="M0,48 C50,42 90,34 130,24 C170,14 220,7 290,3 L290,52 L0,52Z" fill="url(#sg1)" />
            <path d="M0,48 C50,42 90,34 130,24 C170,14 220,7 290,3" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="290" cy="3" r="4" fill="#2563EB" />
          </svg>
          <div className={styles.l}>Revenue trend — 6 months</div>
        </div>
      </div>
    </div>
  )
}

function ModuleViz({ id, color, bg }: { id: string; color: string; bg: string }) {
  const shared = { padding: 14, background: bg, borderTop: '1.5px solid #E5E7EB', minHeight: 200 }
  const cardRow = (items: { label: string; value: string; sub?: string; dot?: string }[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map(item => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 9 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {item.dot && <div style={{ width: 24, height: 24, borderRadius: '50%', background: item.dot + '22', border: `1.5px solid ${item.dot}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: item.dot }}>{item.label[0]}</div>}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{item.label}</div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: color, fontFamily: 'var(--font-mono), monospace' }}>{item.value}</div>
        </div>
      ))}
    </div>
  )
  const barChart = (rows: [string, number, string][]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map(([l, w, c]) => (
        <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 9, color: '#4B5563', width: 64, fontFamily: 'var(--font-mono), monospace' }}>{l}</div>
          <div style={{ flex: 1, height: 7, background: '#EBEBF4', borderRadius: 99 }}><div style={{ width: `${w}%`, height: '100%', background: c, borderRadius: 99 }} /></div>
          <div style={{ fontSize: 9, fontWeight: 700, color: c, width: 28, textAlign: 'right', fontFamily: 'var(--font-mono), monospace' }}>{w}%</div>
        </div>
      ))}
    </div>
  )

  const content: Record<string, React.ReactNode> = {
    dashboard: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6 }}>
          {[['248', C.ind, 'Active Leads'], ['7.2L', C.grn, 'Revenue']].map(([v, c, l]) => (
            <div key={l} style={{ background: '#fff', border: `1.5px solid ${c}44`, borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: c as string }}>{v}</div>
              <div style={{ fontSize: 8, color: '#9CA3AF', fontFamily: 'var(--font-mono), monospace' }}>{l}</div>
            </div>
          ))}
        </div>
        {barChart([['New', 100, C.ind], ['Contacted', 75, C.vio], ['Qualified', 38, C.grn], ['Converted', 12, C.amb]])}
      </div>
    ),
    leads: cardRow([
      { label: 'Rahul Mehta', value: 'Contacted', dot: '#36B1F2' },
      { label: 'Sneha Patel', value: 'Qualified', dot: '#0EA5E9' },
      { label: 'Arjun Sharma', value: 'Converted', dot: '#2563EB' },
      { label: 'Priya Nair', value: 'New', dot: '#60A5FA' },
    ]),
    orders: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 4 }}>
          {[['2.95L', color], ['59K', '#36B1F2'], ['0', '#059669']].map(([v, c], i) => (
            <div key={i} style={{ background: '#fff', border: `1.5px solid ${c}44`, borderRadius: 8, padding: '8px 4px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: c }}>{v}</div>
            </div>
          ))}
        </div>
        {[
          { label: 'Amit Verma — iPhone 15 Pro', value: '1,34,900' },
          { label: 'Riya Sharma — MacBook Air M2', value: '1,14,900' },
          { label: 'Karan Singh — AirPods Pro 2', value: '26,900' },
        ].map(r => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 9 }}>
            <div style={{ fontSize: 10, color: '#111827', fontWeight: 600 }}>{r.label}</div>
            <div style={{ fontSize: 11, fontWeight: 900, color: '#0EA5E9' }}>{r.value}</div>
          </div>
        ))}
      </div>
    ),
    incentives: (
      <div>
        <div style={{ background: '#fff', border: `1.5px solid ${color}44`, borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 9, color: '#9CA3AF', fontFamily: 'var(--font-mono), monospace', marginBottom: 8 }}>1,20,000 DEAL CASCADE</div>
          {CASCADE_ROLES.map((r, i) => (
            <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < 4 ? 6 : 0 }}>
              <div style={{ fontSize: 9, color: '#4B5563', width: 120 }}>{r.role}</div>
              <div style={{ flex: 1, height: 6, background: '#EBEBF4', borderRadius: 99 }}><div style={{ width: `${r.w}%`, height: '100%', background: r.color, borderRadius: 99 }} /></div>
              <div style={{ fontSize: 10, fontWeight: 800, color: r.color, minWidth: 44, textAlign: 'right' }}>{r.amt}</div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #E5E7EB', marginTop: 8, paddingTop: 8 }}>
            <span style={{ fontSize: 11, color: '#4B5563', fontWeight: 600 }}>Total paid</span>
            <span style={{ fontSize: 14, fontWeight: 900, color: '#0EA5E9' }}>14,350</span>
          </div>
        </div>
      </div>
    ),
    logs: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[
          { name: 'Ravi Kumar', role: 'Sales Exec', amt: '8,094', pct: '6%', ok: true },
          { name: 'Amit Verma', role: 'Store Mgr', amt: '3,373', pct: '2.5%', ok: true },
          { name: 'Vikram B.', role: 'Cluster Mgr', amt: '2,024', pct: '1.5%', ok: false },
        ].map(r => (
          <div key={r.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 9, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#111827' }}>{r.name}</div>
              <div style={{ fontSize: 8, color: '#9CA3AF', fontFamily: 'var(--font-mono), monospace' }}>{r.role} · {r.pct}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: '#0EA5E9' }}>{r.amt}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: r.ok ? '#0EA5E9' : '#60A5FA', fontFamily: 'var(--font-mono), monospace' }}>{r.ok ? 'Credited' : 'Pending'}</div>
            </div>
          </div>
        ))}
      </div>
    ),
    review: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 4 }}>
          {[['12', '#60A5FA', 'Pending'], ['47', '#0EA5E9', 'Approved'], ['3', '#1E40AF', 'Flagged']].map(([v, c, l]) => (
            <div key={l} style={{ background: '#fff', border: `1.5px solid ${c}44`, borderRadius: 8, padding: '8px 4px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: c }}>{v}</div>
              <div style={{ fontSize: 8, color: '#9CA3AF', fontFamily: 'var(--font-mono), monospace' }}>{l}</div>
            </div>
          ))}
        </div>
        {[
          { name: 'Arjun Singh', amt: '42,500', action: 'Review', c: '#60A5FA' },
          { name: 'Priya Nair', amt: '8,200', action: 'Approved', c: '#0EA5E9' },
          { name: 'Raju Verma', amt: '71,000', action: 'Flagged', c: '#1E40AF' },
        ].map(r => (
          <div key={r.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 9 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{r.name}</div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: '#111827' }}>{r.amt}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: r.c, fontFamily: 'var(--font-mono), monospace' }}>{r.action}</div>
            </div>
          </div>
        ))}
      </div>
    ),
    leaderboard: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[
          { rank: 1, name: 'Ravi Kumar', store: 'Koramangala', rev: '4.8L', trophy: '🥇' },
          { rank: 2, name: 'Amit Sharma', store: 'Andheri', rev: '3.2L', trophy: '🥈' },
          { rank: 3, name: 'Sneha Patel', store: 'T.Nagar', rev: '2.9L', trophy: '🥉' },
          { rank: 4, name: 'Vikram Bose', store: 'FC Road', rev: '1.7L', trophy: '4' },
          { rank: 5, name: 'Kiran Singh', store: 'CP Store', rev: '1.1L', trophy: '5' },
        ].map((r, i) => (
          <div key={r.rank} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: i === 0 ? color + '10' : '#fff', border: `1.5px solid ${i === 0 ? color : '#E5E7EB'}`, borderRadius: 9 }}>
            <div style={{ width: 22, textAlign: 'center', fontSize: i < 3 ? 16 : 11, color: '#9CA3AF' }}>{r.trophy}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{r.name}</div>
              <div style={{ fontSize: 8, color: '#9CA3AF', fontFamily: 'var(--font-mono), monospace' }}>{r.store}</div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 900, color: i === 0 ? color : '#111827' }}>{r.rev}</div>
          </div>
        ))}
      </div>
    ),
    rules: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {[
          { name: 'Enterprise Bonus', roles: ['SE 6%', 'SM 2.5%', 'CM 1.5%'], active: true },
          { name: 'Premium Plan', roles: ['SE 4%', 'SM 2%', 'CM 1%'], active: true },
          { name: 'Basic Plan', roles: ['SE 2.5%', 'SM 1%', 'CM 0.5%'], active: false },
        ].map(rule => (
          <div key={rule.name} style={{ padding: '10px 12px', background: '#fff', border: `1.5px solid ${rule.active ? color : '#E5E7EB'}`, borderRadius: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{rule.name}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: rule.active ? '#0EA5E9' : '#9CA3AF', fontFamily: 'var(--font-mono), monospace' }}>{rule.active ? 'Active' : 'Inactive'}</div>
            </div>
            <div style={{ display: 'flex', gap: 5 }}>
              {rule.roles.map(r => <div key={r} style={{ fontSize: 8, color: color, background: color + '11', border: `1px solid ${color}33`, borderRadius: 5, padding: '2px 6px', fontFamily: 'var(--font-mono), monospace' }}>{r}</div>)}
            </div>
          </div>
        ))}
      </div>
    ),
    reports: barChart([['Walk-In', 42, color], ['Website', 28, '#36B1F2'], ['Referral', 18, '#059669'], ['Cold Call', 12, '#D97706']]),
    config: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {[
          { cat: 'Warranty', q: 'Q1 2025', slabs: ['SE 4%', 'SM 2%', 'CM 1%'], locked: false },
          { cat: 'Trade-In', q: 'Q1 2025', slabs: ['SE 3%', 'SM 1.5%'], locked: false },
          { cat: 'Accessories', q: 'Q4 2024', slabs: ['SE 2%', 'SM 1%'], locked: true },
        ].map(c => (
          <div key={c.cat} style={{ padding: '9px 11px', background: '#fff', border: `1.5px solid ${c.locked ? '#E5E7EB' : color}44`, borderRadius: 9, opacity: c.locked ? 0.65 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{c.cat}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: c.locked ? '#9CA3AF' : '#059669', fontFamily: 'var(--font-mono), monospace' }}>{c.locked ? 'Locked' : c.q}</div>
            </div>
            <div style={{ display: 'flex', gap: 5 }}>
              {c.slabs.map(s => <div key={s} style={{ fontSize: 8, color: color, background: color + '11', borderRadius: 5, padding: '2px 6px', fontFamily: 'var(--font-mono), monospace' }}>{s}</div>)}
            </div>
          </div>
        ))}
      </div>
    ),
    hierarchy: (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        {[
          { label: 'Zone Head', name: 'Rajesh Kapoor', color: '#2563EB', w: 186 },
          { label: 'Regional Mgr', name: 'Meera Krishnan', color: '#36B1F2', w: 172 },
          { label: 'Cluster Mgr', name: '0EA5E9', color: '#0EA5E9', w: 158 },
          { label: 'Store Manager', name: 'Amit Verma', color: '#059669', w: 144 },
          { label: 'Sales Exec', name: 'Ravi Kumar', color: '#D97706', w: 130 },
        ].map((t, i) => (
          <div key={t.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ border: `2px solid ${t.color}55`, borderRadius: 9, padding: '7px 12px', width: t.w, background: `${t.color}08`, textAlign: 'center' }}>
              <div style={{ fontSize: 7, fontWeight: 700, color: '#fff', background: t.color, borderRadius: 3, padding: '1px 6px', marginBottom: 3, fontFamily: 'var(--font-mono), monospace', textTransform: 'uppercase', display: 'inline-block' }}>{t.label}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{t.name}</div>
            </div>
            {i < 4 && <div style={{ width: 1, height: 10, borderLeft: `2px dashed ${t.color}55` }} />}
          </div>
        ))}
      </div>
    ),
  }
  return <div style={shared}>{content[id] || null}</div>
}

export function HeroSection({ setContactForm, compact = false }: { setContactForm: any; compact?: boolean }) {
  return (
    <section className={`${styles.heroSection} ${compact ? styles.heroCompact : ''}`}>
      <div className={styles.glow} />
      <div className={styles.content}>
        <div className={styles.heroText}>
          <div className={styles.chip}>Solutions · Socommerz SalesCRM</div>
          <h1>
            The Sales OS for<br /><span className={homeStyles["span-gradient"]}>Premium Global Retailers</span>
          </h1>
          <p>
            End-to-end lead management, automatic incentive payouts, and live performance analytics — built for complex, multi-tier sales organisations.
          </p>
          {compact ? (
            <ul className={styles["feature-list"]}>
              {HERO_FEATURES.map((item, index) => (
                <li key={index} className={styles["feature-item"]}>
                  <span className={styles["check-icon"]}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {item.icon}
                    </svg>
                  </span>
                  <span className={styles["feature-text"]}>{item.label}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.tagCloud}>
              {['Leads & Pipeline', 'Incentive Automation', 'Admin Review', 'Leaderboards', 'Reports', 'Config'].map(p => (
                <span key={p}>{p}</span>
              ))}
            </div>
          )}
          {/* <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            <div onClick={() => setContactForm(true)}>
              <Button text='Get Started' classname='button-wrapper' />
            </div>
            <a href="#modules" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 22px', borderRadius:10, background:'transparent', color:'#4B5563', fontSize:14, fontWeight:600, border:'2px solid #D1D5DB', textDecoration:'none' }}>Explore Modules</a>
          </div> */}
        </div>
        <div className={styles.vizContainer}>
          <LeadSystemVisual />
        </div>
      </div>

      {!compact && (
        <div className={styles.statsBar}>
          {[{ v: '248', l: 'Leads Managed' }, { v: '50+', l: 'Active Nodes' }, { v: '11', l: 'Platform Modules' }, { v: '0', l: 'Manual Calculations' }].map((s, i) => (
            <div key={i} className={styles.statItem}>
              <div className={styles.val}>{s.v}</div>
              <div className={styles.label}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {!compact && (
        <div className={styles.ticker}>
          <div style={{ display: 'flex', gap: '40px', whiteSpace: 'nowrap', animation: `${styles.ticker} 20s linear infinite` }}>
            {['Dashboard', 'Lead Management', 'Orders', 'Incentives', 'Reports', 'Configuration', 'Dashboard', 'Lead Management', 'Orders', 'Incentives', 'Reports', 'Configuration'].map((t, i) => (
              <span key={i} style={{ fontSize: '13px', fontWeight: 600, color: '#4B5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t}</span>
            ))}
          </div>
        </div>
      )}

    </section >
  )
}

function ModuleGrid() {
  const [active, setActive] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null!)
  const vis = useVisible(ref as React.RefObject<HTMLElement>)
  return (
    <section id="modules" ref={ref} className={styles.modulesGrid}>
      <div className={styles.header}>
        <div className={styles.chip} style={{ margin: '0 auto 16px' }}>11 Modules · Complete Coverage</div>
        <h2 className={styles.sH2} style={{ marginBottom: 12 }}>Every module your<br /><span className={styles.gradText}>sales team needs</span></h2>
        <p>From the first enquiry to the final incentive credit — every step managed in one platform.</p>
      </div>
      <div className={styles.grid}>
        {MODULES.map((m, i) => (
          <div key={m.id}
            onMouseEnter={() => setActive(m.id)} onMouseLeave={() => setActive(null)}
            className={`${styles.moduleCard} ${active === m.id ? styles.active : ''}`}
            style={{
              borderColor: active === m.id ? m.color : '#E5E7EB',
              boxShadow: active === m.id ? `4px 4px 0 ${m.color}` : 'none',
              opacity: vis ? 1 : 0,
              transitionDelay: `${i * 45}ms`
            }}>
            <div className={styles.accentBar} style={{ background: m.color, opacity: active === m.id ? 1 : 0.25 }} />
            <div className={styles.main}>
              <div className={styles.icon}>{m.icon}</div>
              <div className={styles.badge} style={{ border: `1.5px solid ${m.color}44`, color: m.color, background: `${m.color}10` }}>{m.label}</div>
              <h3>{m.title}</h3>
              <p>{m.desc}</p>
              <div className={styles.stats}>
                {m.stats.map(s => (
                  <div key={s.l} className={styles.item}>
                    <div className={styles.v} style={{ color: m.color }}>{s.v}</div>
                    <div className={styles.l}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <ModuleViz id={m.id} color={m.color} bg={m.bg} />
          </div>
        ))}
      </div>
    </section>
  )
}

function CascadeSection() {
  const ref = useRef<HTMLDivElement>(null!)
  const vis = useVisible(ref as React.RefObject<HTMLElement>)
  return (
    <section ref={ref} className={styles.cascadeSection}>
      <div className={styles.container}>
        <div className={styles.textSide}>
          <div className={styles.chip}>Incentive Engine</div>
          <h2 className={styles.sH2}>One sale.<br /><span className={styles.gradText}>Five people paid.</span></h2>
          <p>Every order triggers an automatic incentive cascade across all five hierarchy levels — from the Sales Executive who closed the deal all the way up to the Area Manager. No spreadsheets. No month-end calculations. No disputes.</p>
          <div className={styles.exampleCard}>
            <div className={styles.exTitle}>Example: 1,20,000 Enterprise Deal</div>
            {CASCADE_ROLES.map((r, i) => (
              <div key={r.role} className={styles.roleRow}>
                <div className={styles.labelCol}>
                  <div className={styles.dot} style={{ background: r.color }} />
                  <div className={styles.role}>{r.role}</div>
                  <div className={styles.pct}>{r.pct}</div>
                </div>
                <div className={styles.barContainer}>
                  <div className={styles.bar} style={{ width: vis ? `${r.w}%` : '0%', background: r.color, transitionDelay: `${400 + i * 100}ms` }} />
                </div>
                <div className={styles.amt} style={{ color: r.color }}>{r.amt}</div>
              </div>
            ))}
            <div className={styles.totalRow}>
              <span>Total Incentive Paid</span>
              <span className={styles.amt}>14,350</span>
            </div>
          </div>
        </div>
        <div className={styles.hierarchyCard}>
          <div className={styles.title}>Organisation Hierarchy</div>
          {[
            { label: 'Zone Head', name: 'Rajesh Kapoor', sub: 'West Zone', color: C.ind, w: 196 },
            { label: 'Regional Mgr', name: 'Meera Krishnan', sub: 'Mumbai Region', color: C.vio, w: 180 },
            { label: 'Cluster Mgr', name: 'Vikram Bose', sub: 'Mumbai Central', color: C.sky, w: 164 },
            { label: 'Store Manager', name: 'Amit Verma', sub: 'Koramangala', color: C.grn, w: 148 },
            { label: 'Sales Exec', name: 'Ravi Kumar', sub: 'Andheri Store', color: C.amb, w: 132 },
          ].map((t, i) => (
            <div key={t.label} className={styles.node}>
              <div className={styles.box} style={{ borderColor: `${t.color}55`, width: t.w, background: `${t.color}08` }}>
                <div className={styles.label} style={{ background: t.color }}>{t.label}</div>
                <div className={styles.name}>{t.name}</div>
                <div className={styles.sub}>{t.sub}</div>
              </div>
              {i < 4 && <div className={styles.line} style={{ borderColor: `${t.color}44` }} />}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function StoresSection() {
  const ref = useRef<HTMLDivElement>(null!)
  const vis = useVisible(ref as React.RefObject<HTMLElement>)
  return (
    <section ref={ref} className={styles.storesSection}>
      <div className={styles.container}>
        <div className={styles.textSide}>
          <div className={styles.chip}>Node Performance</div>
          <h2 className={styles.sH2}>Every node.<br /><span className={styles.gradText}>Side by side.</span></h2>
          <p>Compare all locations on one screen. Revenue, rankings, quarterly targets — visible to managers at every level. Transparent data creates competitive teams.</p>
          <div className={styles.targetsBox}>
            <div className={styles.title}>Q1 Targets — Bangalore</div>
            {[['Products', '40 units'], ['Contract', '25 units'], ['Retention', '60 units'], ['Growth', '15 units']].map(([k, v], i) => (
              <div key={k} className={styles.row}>
                <span>{k}</span><span className={styles.v}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.listSide}>
          {STORES.map((s, i) => (
            <div key={s.city} className={styles.storeCard}>
              <div className={styles.rank}>#{s.rank}</div>
              <div className={styles.info}>
                <div className={styles.city}>{s.city}</div>
                <div className={styles.state}>{s.state}</div>
              </div>
              <div className={styles.bar}>
                <div className={styles.fill} style={{ width: vis ? `${s.pct}%` : '0%', background: 'linear-gradient(90deg,#2563EB,#36B1F2)', transitionDelay: `${200 + i * 100}ms` }} />
              </div>
              <div className={styles.rev}>{s.revenue}</div>
            </div>
          ))}
          <div className={styles.totalBar}>
            <span>Q1 Total Revenue</span>
            <span className={styles.amt}>2,95,000</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function FeaturesSection() {
  const ref = useRef<HTMLDivElement>(null!)
  const vis = useVisible(ref as React.RefObject<HTMLElement>)
  return (
    <section ref={ref} className={styles.featuresSection}>
      <div className={styles.header}>
        <div className={styles.chip} style={{ margin: '0 auto 16px' }}>Platform Highlights</div>
        <h2 className={styles.sH2}>Designed for how<br /><span className={styles.gradText}>enterprise retail actually works</span></h2>
      </div>
      <div className={styles.grid}>
        {FEATURES.map((f, i) => (
          <div key={f.title}
            className={styles.featureCard}
            style={{ opacity: vis ? 1 : 0, transitionDelay: `${i * 55}ms` }}>
            <div className={styles.icon}>{f.icon}</div>
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

// function VideoSection() {
//   return (
//     <section className={styles.videoSection}>
//       <div className={styles.header}>
//         <div className={styles.chip} style={{ margin: '0 auto 16px' }}>Platform Walkthrough</div>
//         <h2 className={styles.sH2} style={{ marginBottom: 12 }}>See it in <span className={styles.gradText}>5 minutes</span></h2>
//         <p>A complete walkthrough of all 11 modules — from lead capture to incentive credit.</p>
//       </div>
//       <div className={styles.grid}>
//         <div className={styles.videoPlayer}>
//           <div className={styles.overlay}>
//             <div className={styles.playBtn}>
//               <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32"><path d="M8 5v14l11-7z" /></svg>
//             </div>
//             <div className={styles.vTitle}>Socommerz SalesCRM — Full Platform Walkthrough</div>
//             <div className={styles.vMeta}>5:15 min · All 11 modules</div>
//           </div>
//         </div>
//         <div className={styles.walkthroughList}>
//           {[['0:00', 'The Challenge'], ['0:18', 'Dashboard & KPIs'], ['0:52', 'Lead Management'], ['1:30', 'Orders'], ['2:05', 'My Incentives'], ['2:45', 'Incentive Logs'], ['3:10', 'Admin Review'], ['3:38', 'Leaderboard'], ['4:08', 'Incentive Rules'], ['4:32', 'Reports'], ['4:55', 'Configuration']].map(([t, l]) => (
//             <div key={l} className={styles.item}>
//               <span className={styles.time}>{t}</span>
//               <span className={styles.label}>{l}</span>
//             </div>
//           ))}
//         </div>
//       </div>
//     </section>
//   )
// }

export default function SalesCRM() {
  const { setContactForm }: any = useContext(AppContext)

  return (
    <>
      <Head>
        <title>Sales CRM | Socommerz — Advanced Retail Lead & Incentive OS</title>
        <meta name="title" content="Sales CRM | Socommerz — Advanced Retail Lead & Incentive OS" />
        <meta
          name="description"
          content="The Sales OS for premium global retailers. End-to-end lead management, automatic incentive payouts, and live performance analytics built for multi-tier retail organisations."
        />
        <meta
          name="keywords"
          content="sales crm, lead management, retail incentive automation, sales executive leaderboard, retail sales OS, incentive payout system, socommerz crm"
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.socommerz.com/sales-crm" />
        <link rel="alternate" href="https://www.socommerz.com/sales-crm" hrefLang="en-in" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socommerz.com/sales-crm/" />
        <meta property="og:title" content="Sales CRM | Socommerz — Advanced Retail Lead & Incentive OS" />
        <meta
          property="og:description"
          content="The Sales OS for premium global retailers. End-to-end lead management, automatic incentive payouts, and live performance analytics built for multi-tier retail organisations."
        />
        <meta property="twitter:url" content="https://www.socommerz.com/sales-crm/" />
        <meta property="twitter:title" content="Sales CRM | Socommerz — Advanced Retail Lead & Incentive OS" />
        <meta
          property="twitter:description"
          content="The Sales OS for premium global retailers. End-to-end lead management, automatic incentive payouts, and live performance analytics built for multi-tier retail organisations."
        />
      </Head>
      <main className={styles.salesCrmWrapper}>
        <HeroSection setContactForm={setContactForm} />
        <ModuleGrid />
        <CascadeSection />
        <StoresSection />
        <FeaturesSection />
        {/* <VideoSection /> */}
      </main>
    </>
  )
}
